import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, Target, Sparkles, Building2, GraduationCap, Heart } from "lucide-react";

const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

export default function AboutUs() {
  const navigate = useNavigate();

  const values = [
    {
      icon: Users,
      title: "Community First",
      description: "We believe in building a supportive ecosystem where talent, trainers, and enterprises grow together."
    },
    {
      icon: Target,
      title: "Equal Opportunity",
      description: "Every artist deserves a fair chance. We connect talent with opportunities regardless of background."
    },
    {
      icon: Heart,
      title: "Passion Driven",
      description: "We're built by entertainment industry enthusiasts who understand the dreams and challenges of artists."
    }
  ];

  const ecosystem = [
    {
      icon: Sparkles,
      title: "For Talent",
      color: "from-violet-500 to-pink-500",
      points: [
        "Build a professional portfolio",
        "Get discovered by casting directors",
        "Access exclusive auditions and opportunities"
      ]
    },
    {
      icon: GraduationCap,
      title: "For Trainers",
      color: "from-orange-500 to-amber-500",
      points: [
        "Showcase your expertise and credentials",
        "Connect with aspiring talent",
        "Grow your student base and income"
      ]
    },
    {
      icon: Building2,
      title: "For Enterprises",
      color: "from-blue-500 to-indigo-500",
      points: [
        "Discover verified, quality talent",
        "Streamline your casting process",
        "Manage projects efficiently"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="px-4 py-4 bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg object-cover" />
            <span className="font-display text-xl font-bold text-gray-900">TalentMeet</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-violet-600 via-purple-600 to-pink-600 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            About TalentMeet
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
            Where Talent Meets Opportunity — A unified platform connecting artists, trainers, and enterprises in the entertainment industry.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              TalentMeet is on a mission to democratize access to opportunities in the entertainment industry. 
              We're building a bridge between aspiring artists and the professionals who can help them succeed — 
              whether through training, mentorship, or direct casting opportunities.
            </p>
          </div>

          <div className="bg-gradient-to-r from-violet-50 to-pink-50 rounded-2xl p-8 border border-violet-100">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">The Problem We Solve</h3>
            <p className="text-gray-600 mb-4">
              The entertainment industry has traditionally been opaque and difficult to navigate. Talented individuals 
              often struggle to find legitimate opportunities, while production houses and agencies spend countless 
              hours searching for the right talent.
            </p>
            <p className="text-gray-600">
              TalentMeet creates a transparent, efficient marketplace where talent can showcase their skills, 
              trainers can build their reputation, and enterprises can discover the perfect candidates for their projects.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value) => (
              <div key={value.title} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">The TalentMeet Ecosystem</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We serve three key stakeholders in the entertainment industry, creating a symbiotic ecosystem where everyone benefits.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {ecosystem.map((item) => (
              <div key={item.title} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center mb-4`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{item.title}</h3>
                <ul className="space-y-2">
                  {item.points.map((point, idx) => (
                    <li key={idx} className="text-gray-600 text-sm flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2 flex-shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-violet-600 to-pink-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to Join the Community?</h2>
          <p className="text-white/90 mb-8">
            Whether you're a talented artist, an experienced trainer, or looking to cast your next project — TalentMeet is here for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-violet-600 hover:bg-gray-100 px-8"
              onClick={() => navigate("/")}
            >
              Get Started
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8"
              onClick={() => navigate("/browse")}
            >
              Browse Talents
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
                <span className="font-bold text-white">TalentMeet</span>
              </div>
              <p className="text-sm">
                Where Talent Meets Opportunity. Connecting artists, trainers, and enterprises in the entertainment industry.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate("/browse")} className="hover:text-white transition-colors">Browse Talents</button></li>
                <li><button onClick={() => navigate("/services")} className="hover:text-white transition-colors">Opportunities</button></li>
                <li><button onClick={() => navigate("/login")} className="hover:text-white transition-colors">Sign In</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate("/about")} className="hover:text-white transition-colors">About Us</button></li>
                <li><a href="mailto:support@talentmeet.in" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => navigate("/terms")} className="hover:text-white transition-colors">Terms of Service</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm">&copy; 2026 TalentMeet. All rights reserved.</p>
            <p className="text-sm">Support: <a href="mailto:support@talentmeet.in" className="text-violet-400 hover:text-violet-300">support@talentmeet.in</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
